import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { GuidService } from '../../../services/guid.service';
import { environment } from '../../../services/global-environment';
import { firstValueFrom } from 'rxjs';
import { TemplateFileUploadComponent } from '../../../shared/components/template-file-upload/template-file-upload.component';
import { ShowlistComponent } from '../../list/showlist/showlist.component';

interface Contractor {
  id: string;
  companyName: string;
  uniformNumber: string;
  employeeCount: number;
  industryType: string;
  contractorType: string[];
  responsiblePerson: string;
  contactPhone: string;
  safetyOfficer: string;
  safetyOfficerPhone: string;
  address: string;
  remarks?: string;
}

// 範本檔案介面
interface TemplateFile {
  type: string;
  file?: File;
  uploaded: boolean;
}

// API 範本檔案配置介面
interface TemplateFileConfig {
  type: string;
  templateName: string;
  templateUrl: string;
  acceptedFormats: string[];
  maxFileSize: number;
  required: boolean;
}

// 檔案記錄介面
interface PersonnelFile {
  filetype: string;
  url: string;
  valid: boolean;
  expiryDate?: string; // 新增有效日期欄位
}

// 承攬商員工資料介面 (根據API回傳資料結構)
interface ContractorEmployee {
  id: string;
  companyid: string;
  contractorId: string;
  name: string;
  photo?: string;
  phone: string;
  idNumber: string;
  bloodType: string;
  birthDate: string;
  files: PersonnelFile[];
  emergencyContact: string;
  emergencyPhone: string;
  remarks?: string;
  createdAt: string;
  updatedAt: string;
}

// 舊版人員介面 (保持相容性)
interface Personnel {
  id: string;
  contractorId: string;
  name: string;
  photo?: any;
  photoUrl?: string; // 新增照片 URL 屬性
  phone: string;
  idNumber: string;
  bloodType: string;
  birthDate: string;
  files: PersonnelFile[]; // 統一的檔案陣列
  emergencyContact: string;
  emergencyPhone: string;
  remarks?: string;
}

@Component({
  selector: 'app-contractor-info',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    TemplateFileUploadComponent,
    ShowlistComponent
  ],
  templateUrl: './contractor-info.component.html',
  styleUrl: './contractor-info.component.scss'
})


export class ContractorInfoComponent implements OnInit {
  contractors: Contractor[] = [];
  selectedContractor: Contractor | null = null;
  selectedContractorPersonnel: Personnel[] = [];

  showContractorModal = false;
  showPersonnelModal = false;
  showPhotoDetailModal = false;
  isAddMode = true;
  editingContractor: Contractor | null = null;
  editingPerson: Personnel | null = null;
  selectedPersonForPhoto: Personnel | null = null;

  // 新增載入狀態
  isSaving = false;
  isDeleting = false;
  isLoadingList = false;
  isLoadingPersonnel = false; // 新增員工載入狀態

  // 篩選相關屬性
  filteredContractors: Contractor[] = [];
  filterContractorType: string = '';
  searchKeyword: string = '';

  // 照片上傳相關屬性
  selectedPhotoUrl: string | null = null;

  // API 相關屬性
  private apiUrl = environment.apiUrl;
  private companyid = environment.companyid;
  assistantNo: string = '';
  componentName: string = 'contractor-info';

  // 表單ID - 根據公司ID動態設定
  contractorFormId: string = '';

  // 範本檔案相關屬性 (改為動態載入)
  templateFileConfigs: TemplateFileConfig[] = [];
  templateFiles: { [key: string]: TemplateFile } = {};

  // 人員範本檔案相關屬性
  personnelTemplateFileConfigs: TemplateFileConfig[] = [];
  personnelTemplateFiles: { [key: string]: TemplateFile | undefined } = {};


  contractorForm: FormGroup;
  personnelForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private guidService: GuidService,
    private http: HttpClient,
    private route: ActivatedRoute
  ) {
    this.contractorForm = this.fb.group({
      companyName: ['', Validators.required],
      uniformNumber: ['', Validators.required],
      employeeCount: [0, Validators.required],
      industryType: ['', Validators.required],
      contractorType: [[], Validators.required],
      responsiblePerson: ['', Validators.required],
      contactPhone: ['', Validators.required],
      safetyOfficer: ['', Validators.required],
      safetyOfficerPhone: ['', Validators.required],
      address: ['', Validators.required],
      remarks: ['']
    });

    this.personnelForm = this.fb.group({
      name: ['', Validators.required],
      phone: ['', Validators.required],
      idNumber: ['', Validators.required],
      bloodType: ['', Validators.required],
      birthDate: ['', Validators.required],
      trainingCertExpiryDate: [''],
      laborInsuranceExpiryDate: [''],
      groupInsuranceExpiryDate: [''],
      healthCheckExpiryDate: [''],
      emergencyContact: ['', Validators.required],
      emergencyPhone: ['', Validators.required],
      remarks: [''], // 新增備註欄位
      photo: [''], // 新增照片欄位 (儲存 blobName)
      trainingCertFile: [''], // 六小時教育訓練證明檔案 blobName
      laborInsuranceFile: [''], // 勞保證明檔案 blobName
      groupInsuranceFile: [''], // 團保證明檔案 blobName
      healthCheckFile: [''] // 健康檢查檔案 blobName
    });
  }

  ngOnInit(): void {
    // 取得路由參數
    this.assistantNo = this.route.snapshot.queryParams['id'] || '';

    // 根據公司ID設定表單ID
    this.setContractorFormId();

    // 載入範本檔案配置
    this.loadTemplateFileConfigs('contractor');

    // 載入人員範本檔案配置
    this.loadTemplateFileConfigs('contractor-employee');

    // 載入承攬商列表
    this.loadContractorList();
  }

  /**
   * 根據公司ID設定承攬商表單ID
   */
  private setContractorFormId(): void {
    switch (this.companyid.toLowerCase()) {
      case 'jsai':
        this.contractorFormId = '9145135a-1b35-484d-8082-579257b205c0';
        break;
      case 'eirgenix':
        this.contractorFormId = '823ab971-a52e-4a27-99cd-bcc4494ab468';
        break;
      default:
        // 預設使用 JSAI 的表單ID
        this.contractorFormId = '';
        console.warn(`未設定公司 ${this.companyid} 的表單ID，使用預設值`);
        break;
    }
    console.log(`設定承攬商表單ID為: ${this.contractorFormId} (公司: ${this.companyid})`);
  }

  /**
   * 載入範本檔案配置
   * @param targetType 目標類型，'contractor' 或 'contractor-employee'
   */
  async loadTemplateFileConfigs(targetType: 'contractor' | 'contractor-employee' = 'contractor'): Promise<void> {
    try {
      const url = `${this.apiUrl}/api/contractor-files-template/file-template`;
      const params: any = {
        companyid: this.companyid,
        assistantNo: this.assistantNo,
        componentName: this.componentName
      };

      const response = await firstValueFrom(this.http.get<any>(url, { params }));


      if (response.success && response.data && response.data.length > 0) {
        // 篩選出指定類型的範本配置
        const filteredConfigs = response.data.filter((config: any) => config.type === targetType);

        if (filteredConfigs.length > 0) {
          // 處理資料格式，將 acceptedFormats 字串轉換為陣列，maxFileSize 轉換為數字
          const processedConfigs = filteredConfigs.map((config: any) => ({
            ...config,
            acceptedFormats: this.processAcceptedFormats(config.acceptedFormats),
            maxFileSize: config.maxFileSize ?
              parseInt(config.maxFileSize.toString().replace(/[^\d]/g, '')) || 10 :
              10
          }));

          // 根據類型設定對應的屬性
          if (targetType === 'contractor') {
            this.templateFileConfigs = processedConfigs;
            // 初始化 templateFiles 物件
            this.templateFiles = {};
            this.templateFileConfigs.forEach(config => {
              this.templateFiles[config.type] = {
                type: config.type,
                uploaded: false
              };
            });
            console.log('承攬商範本檔案配置載入成功:', this.templateFileConfigs);
          } else if (targetType === 'contractor-employee') {
            this.personnelTemplateFileConfigs = processedConfigs;
            // 初始化 personnelTemplateFiles 物件
            this.personnelTemplateFiles = {};
            this.personnelTemplateFileConfigs.forEach(config => {
              this.personnelTemplateFiles[config.type] = {
                type: config.type,
                uploaded: false
              };
            });
            console.log('人員範本檔案配置載入成功:', this.personnelTemplateFileConfigs);
          }
        } else {
          console.log(`沒有找到 ${targetType} 類型的範本檔案配置資料`);
          this.resetTemplateConfigs(targetType);
        }
      } else {
        console.log('沒有找到範本檔案配置資料');
        this.resetTemplateConfigs(targetType);
      }
    } catch (error) {
      console.error(`載入 ${targetType} 範本檔案配置失敗:`, error);
      this.resetTemplateConfigs(targetType);
    }
  }

  /**
   * 重置範本檔案配置
   * @param targetType 目標類型
   */
  private resetTemplateConfigs(targetType: 'contractor' | 'contractor-employee'): void {
    if (targetType === 'contractor') {
      this.templateFileConfigs = [];
      this.templateFiles = {};
    } else if (targetType === 'contractor-employee') {
      this.personnelTemplateFileConfigs = [];
      this.personnelTemplateFiles = {};
    }
  }


  loadContractorList(): void {
    this.isLoadingList = true; // 開始載入
    const companyid = environment.clientId;
    const apiUrl = `${environment.apiUrl}/api/contractor/list`;

    console.log('Loading contractor list from API...');

    this.http.get<any>(apiUrl, {
      params: { companyid: companyid }
    }).subscribe({
      next: (response) => {
        this.isLoadingList = false; // 結束載入
        if (response.success && response.data) {
          console.log('承攬商列表載入成功:', response.data);

          // 轉換 API 資料為本機格式
          this.contractors = response.data.map((item: any) => ({
            id: item.id,
            companyName: item.companyName,
            uniformNumber: item.uniformNumber,
            employeeCount: item.employeeCount,
            industryType: item.industryType,
            contractorType: item.contractorType || [],
            responsiblePerson: item.responsiblePerson,
            contactPhone: item.contactPhone,
            safetyOfficer: item.safetyOfficer,
            safetyOfficerPhone: item.safetyOfficerPhone,
            address: item.address,
            remarks: item.remarks || ''
          }));

          // 初始化每個承攬商的人員資料
          this.contractors.forEach(contractor => {
            if (!this.personnelData[contractor.id]) {
              this.personnelData[contractor.id] = [];
            }
          });

          console.log('承攬商列表處理完成:', this.contractors);

          // 初始化篩選列表
          this.initializeFilters();
        } else {
          console.error('API 回傳錯誤:', response);
          this.loadMockData(); // 如果 API 失敗，載入 Mock 資料
        }
      },
      error: (error) => {
        this.isLoadingList = false; // 結束載入
        console.error('載入承攬商列表失敗:', error);
        console.log('載入 Mock 資料作為備選...');
        this.loadMockData(); // 如果 API 失敗，載入 Mock 資料
      }
    });
  }

  loadMockData(): void {
    // Mock contractors
    const contractor1Id = this.guidService.generateGuid();
    const contractor2Id = this.guidService.generateGuid();

    this.contractors = [
      {
        id: contractor1Id,
        companyName: '信昕資能股份有限公司',
        uniformNumber: '00080210',
        employeeCount: 10,
        industryType: 'I301010 資訊軟體服務業',
        contractorType: ['一般作業(其他)'],
        responsiblePerson: '董芳瑜',
        contactPhone: '02-7724-2067',
        safetyOfficer: '董芳瑜',
        safetyOfficerPhone: '02-7724-2067',
        address: '台中市忠區海路一段100號9樓之2',
        remarks: '主要承攬軟體開發相關業務'
      },
      {
        id: contractor2Id,
        companyName: '台灣資安集團',
        uniformNumber: '12345678',
        employeeCount: 25,
        industryType: 'I301010 資訊軟體服務業',
        contractorType: ['電氣作業'],
        responsiblePerson: '張志明',
        contactPhone: '02-2222-3333',
        safetyOfficer: '林小華',
        safetyOfficerPhone: '02-2222-4444',
        address: '台北市大安區信義路五段7號',
        remarks: '專精於資安技術及電氣設備維護'
      }
    ];

    // 初始化人員資料對應表
    this.personnelData[contractor1Id] = [
      {
        id: this.guidService.generateGuid(),
        contractorId: contractor1Id,
        name: '董芳瑜',
        phone: '0912494951',
        idNumber: 'T22200000',
        bloodType: 'AB',
        birthDate: '1978/08/30',
        files: [
          { filetype: 'trainingCert', url: 'sample_training_cert_1.pdf', valid: true },
          { filetype: 'laborInsurance', url: 'sample_labor_insurance_1.pdf', valid: true },
          { filetype: 'groupInsurance', url: 'sample_group_insurance_1.pdf', valid: true },
          { filetype: 'healthCheck', url: 'sample_health_check_1.pdf', valid: true }
        ],
        emergencyContact: '李OO',
        emergencyPhone: '0988123456'
      },
      {
        id: this.guidService.generateGuid(),
        contractorId: contractor1Id,
        name: '黃湘庭',
        phone: '0930494951',
        idNumber: 'R2220000',
        bloodType: 'B',
        birthDate: '1977/08/23',
        files: [
          { filetype: 'trainingCert', url: 'sample_training_cert_2.pdf', valid: true },
          { filetype: 'laborInsurance', url: 'sample_labor_insurance_2.pdf', valid: true },
          { filetype: 'groupInsurance', url: 'sample_group_insurance_2.pdf', valid: true },
          { filetype: 'healthCheck', url: 'sample_health_check_2.pdf', valid: true }
        ],
        emergencyContact: '王OO',
        emergencyPhone: '0930123456'
      }
    ];

    this.personnelData[contractor2Id] = [

    ];

    // Mock personnel data for the first contractor
    this.selectedContractorPersonnel = [

    ];

    // 初始化篩選列表
    this.initializeFilters();
  }

  // 儲存人員資料的對應表
  private personnelData: { [contractorId: string]: Personnel[] } = {};

  // Tag 型承攬商類別輸入相關屬性
  selectedContractorTypes: string[] = [];
  contractorTypeInput: string = '';
  showContractorTypeDropdown: boolean = false;
  availableContractorTypes: string[] = [
    '一般作業(其他)',
    '高架作業',
    '電氣作業',
    '焊接作業',
    '起重作業',
    '營造作業',
    '清潔作業',
    '保養維修',
    '運輸作業',
    '化學作業'
  ];
  filteredContractorTypeOptions: string[] = [];

  // 篩選相關方法
  initializeFilters(): void {
    this.filteredContractors = [...this.contractors];
    this.filterContractorType = '';
    this.searchKeyword = '';
  }

  applyFilters(): void {
    this.filteredContractors = this.contractors.filter(contractor => {
      // 承攬商類別篩選
      const typeMatch = !this.filterContractorType ||
        contractor.contractorType.includes(this.filterContractorType);

      // 關鍵字搜尋 (公司名稱、負責人、電話、地址)
      const keywordMatch = !this.searchKeyword ||
        contractor.companyName.toLowerCase().includes(this.searchKeyword.toLowerCase()) ||
        contractor.responsiblePerson.toLowerCase().includes(this.searchKeyword.toLowerCase()) ||
        contractor.contactPhone.includes(this.searchKeyword) ||
        contractor.address.toLowerCase().includes(this.searchKeyword.toLowerCase()) ||
        contractor.safetyOfficer.toLowerCase().includes(this.searchKeyword.toLowerCase()) ||
        contractor.safetyOfficerPhone.includes(this.searchKeyword) ||
        contractor.uniformNumber.includes(this.searchKeyword) ||
        contractor.industryType.toLowerCase().includes(this.searchKeyword.toLowerCase());

      return typeMatch && keywordMatch;
    });
  }

  clearSearch(): void {
    this.searchKeyword = '';
    this.applyFilters();
  }

  clearAllFilters(): void {
    this.filterContractorType = '';
    this.searchKeyword = '';
    this.applyFilters();
  }

  // Contractor operations
  viewPersonnel(contractor: Contractor): void {
    this.selectedContractor = contractor;
    this.loadContractorEmployees(contractor.id);
  }

  // 載入承攬商員工資料
  loadContractorEmployees(contractorId: string): void {
    this.isLoadingPersonnel = true;
    this.selectedContractorPersonnel = []; // 清空現有資料

    const companyid = environment.clientId;
    const apiUrl = `${environment.apiUrl}/api/contractor/employee/list`;

    console.log('Loading contractor employees from API...', { companyid, contractorId });

    this.http.get<any>(apiUrl, {
      params: {
        companyid: companyid,
        contractorId: contractorId
      }
    }).subscribe({
      next: async (response) => {
        this.isLoadingPersonnel = false;
        if (response.success && response.data) {
          console.log('承攬商員工列表載入成功:', response.data);

          // 轉換 API 資料為本機格式
          const personnelPromises = response.data.map(async (employee: ContractorEmployee) => {
            const personnel: Personnel = {
              id: employee.id,
              contractorId: employee.contractorId,
              name: employee.name,
              photo: employee.photo,
              phone: employee.phone,
              idNumber: employee.idNumber,
              bloodType: employee.bloodType,
              birthDate: employee.birthDate,
              files: employee.files || [],
              emergencyContact: employee.emergencyContact,
              emergencyPhone: employee.emergencyPhone,
              remarks: employee.remarks || ''
            };

            // 如果有照片，異步獲取照片 URL
            if (employee.photo) {
              try {
                personnel.photoUrl = await this.getSecurePhotoUrl(employee.photo);
              } catch (error) {
                console.error('獲取照片 URL 失敗:', error);
                personnel.photoUrl = undefined;
              }
            }

            return personnel;
          });

          // 等待所有照片 URL 獲取完成
          try {
            const personnelList = await Promise.all(personnelPromises);
            this.selectedContractorPersonnel = personnelList;
            // 更新人員資料對應表
            this.personnelData[contractorId] = this.selectedContractorPersonnel;
            console.log('承攬商員工資料處理完成:', this.selectedContractorPersonnel);
          } catch (error) {
            console.error('處理員工照片時發生錯誤:', error);
            // 即使照片處理失敗，也要顯示基本資料
            this.selectedContractorPersonnel = response.data.map((employee: ContractorEmployee) => ({
              id: employee.id,
              contractorId: employee.contractorId,
              name: employee.name,
              photo: employee.photo,
              phone: employee.phone,
              idNumber: employee.idNumber,
              bloodType: employee.bloodType,
              birthDate: employee.birthDate,
              files: employee.files || [],
              emergencyContact: employee.emergencyContact,
              emergencyPhone: employee.emergencyPhone,
              remarks: employee.remarks || ''
            }));
            // 更新人員資料對應表
            this.personnelData[contractorId] = this.selectedContractorPersonnel;
          }
        } else {
          console.error('API 回傳錯誤:', response);
          // 如果沒有資料，使用空陣列
          this.selectedContractorPersonnel = [];
          this.personnelData[contractorId] = [];
        }
      },
      error: (error) => {
        this.isLoadingPersonnel = false;
        console.error('載入承攬商員工列表失敗:', error);
        // 錯誤處理：使用空陣列或從本地快取載入
        this.selectedContractorPersonnel = this.personnelData[contractorId] || [];
      }
    });
  }

  openContractorModal(mode: 'add' | 'edit', contractor?: Contractor): void {
    this.isAddMode = mode === 'add';
    this.showContractorModal = true;

    if (mode === 'edit' && contractor) {
      this.editingContractor = contractor;

      // 直接使用承攬商類別陣列
      if (contractor.contractorType && Array.isArray(contractor.contractorType)) {
        this.selectedContractorTypes = [...contractor.contractorType];
      } else {
        this.selectedContractorTypes = [];
      }

      this.contractorForm.patchValue({
        companyName: contractor.companyName,
        uniformNumber: contractor.uniformNumber,
        employeeCount: contractor.employeeCount,
        industryType: contractor.industryType,
        contractorType: contractor.contractorType,
        responsiblePerson: contractor.responsiblePerson,
        contactPhone: contractor.contactPhone,
        safetyOfficer: contractor.safetyOfficer,
        safetyOfficerPhone: contractor.safetyOfficerPhone,
        address: contractor.address,
        remarks: contractor.remarks || ''
      });
    } else {
      this.editingContractor = null;
      this.selectedContractorTypes = []; // 移除預設選擇
      this.contractorForm.reset({
        contractorType: [],
        employeeCount: 0
      });
    }

    // 重設輸入狀態
    this.contractorTypeInput = '';
    this.showContractorTypeDropdown = false;
  }

  closeContractorModal(): void {
    this.showContractorModal = false;
    // 重設標籤狀態
    this.selectedContractorTypes = [];
    this.contractorTypeInput = '';
    this.showContractorTypeDropdown = false;
    // 重設載入狀態
    this.isSaving = false;
  }

  saveContractor(): void {
    if (this.contractorForm.invalid) return;

    const formData = this.contractorForm.value;

    if (this.isAddMode) {
      // Add new contractor via API
      this.createContractorViaAPI(formData);
    } else if (this.editingContractor) {
      // Update existing contractor via API
      this.updateContractorViaAPI(this.editingContractor.id, formData);
    }
  }

  private createContractorViaAPI(formData: any): void {
    this.isSaving = true; // 開始載入
    const companyid = environment.companyid; // 使用 clientId 作為 companyid
    const apiUrl = `${environment.apiUrl}/api/contractor/create`;

    // 準備 API 請求資料
    const requestData = {
      companyid: companyid,
      companyName: formData.companyName,
      uniformNumber: formData.uniformNumber,
      employeeCount: formData.employeeCount,
      industryType: formData.industryType,
      contractorType: formData.contractorType, // 這是字串陣列
      responsiblePerson: formData.responsiblePerson,
      contactPhone: formData.contactPhone,
      safetyOfficer: formData.safetyOfficer,
      safetyOfficerPhone: formData.safetyOfficerPhone,
      address: formData.address,
      remarks: formData.remarks || ''
    };

    console.log('Creating contractor via API:', requestData);

    this.http.post<any>(apiUrl, requestData).subscribe({
      next: (response) => {
        this.isSaving = false; // 結束載入
        if (response.success) {
          console.log('承攬商創建成功:', response.data);

          // 將 API 回傳的資料轉換為本機 Contractor 格式
          const newContractor: Contractor = {
            id: response.data.id,
            companyName: response.data.companyName,
            uniformNumber: response.data.uniformNumber,
            employeeCount: response.data.employeeCount,
            industryType: response.data.industryType,
            contractorType: response.data.contractorType,
            responsiblePerson: response.data.responsiblePerson,
            contactPhone: response.data.contactPhone,
            safetyOfficer: response.data.safetyOfficer,
            safetyOfficerPhone: response.data.safetyOfficerPhone,
            address: response.data.address,
            remarks: response.data.remarks
          };

          // 更新本機資料
          this.contractors.push(newContractor);
          this.personnelData[newContractor.id] = [];

          alert('承攬商新增成功！');
          this.closeContractorModal();
          // 重新載入列表以確保資料同步
          this.loadContractorList();
        } else {
          console.error('API 回傳錯誤:', response);
          alert('新增失敗：' + (response.message || '未知錯誤'));
        }
      },
      error: (error) => {
        this.isSaving = false; // 結束載入
        console.error('API 呼叫失敗:', error);
        alert('新增失敗：' + (error.error?.message || error.message || '網路連線錯誤'));
      }
    });
  }

  private updateContractorViaAPI(contractorId: string, formData: any): void {
    this.isSaving = true; // 開始載入
    const companyid = environment.clientId;
    const apiUrl = `${environment.apiUrl}/api/contractor/update`;

    // 準備 API 請求資料
    const requestData = {
      id: contractorId,
      companyid: companyid,
      companyName: formData.companyName,
      uniformNumber: formData.uniformNumber,
      employeeCount: formData.employeeCount,
      industryType: formData.industryType,
      contractorType: formData.contractorType,
      responsiblePerson: formData.responsiblePerson,
      contactPhone: formData.contactPhone,
      safetyOfficer: formData.safetyOfficer,
      safetyOfficerPhone: formData.safetyOfficerPhone,
      address: formData.address,
      remarks: formData.remarks || ''
    };

    console.log('Updating contractor via API:', requestData);

    this.http.put<any>(apiUrl, requestData).subscribe({
      next: (response) => {
        this.isSaving = false; // 結束載入
        if (response.success) {
          console.log('承攬商更新成功:', response.data);

          // 更新本機資料
          const index = this.contractors.findIndex(c => c.id === contractorId);
          if (index !== -1) {
            const updatedContractor: Contractor = {
              id: response.data.id,
              companyName: response.data.companyName,
              uniformNumber: response.data.uniformNumber,
              employeeCount: response.data.employeeCount,
              industryType: response.data.industryType,
              contractorType: response.data.contractorType,
              responsiblePerson: response.data.responsiblePerson,
              contactPhone: response.data.contactPhone,
              safetyOfficer: response.data.safetyOfficer,
              safetyOfficerPhone: response.data.safetyOfficerPhone,
              address: response.data.address,
              remarks: response.data.remarks
            };

            this.contractors[index] = updatedContractor;

            // Update selected contractor if it was the one being edited
            if (this.selectedContractor && this.selectedContractor.id === contractorId) {
              this.selectedContractor = updatedContractor;
            }
          }

          alert('承攬商資料更新成功！');
          this.closeContractorModal();
          // 重新載入列表以確保資料同步
          this.loadContractorList();
        } else {
          console.error('API 回傳錯誤:', response);
          alert('更新失敗：' + (response.message || '未知錯誤'));
        }
      },
      error: (error) => {
        this.isSaving = false; // 結束載入
        console.error('API 呼叫失敗:', error);
        alert('更新失敗：' + (error.error?.message || error.message || '網路連線錯誤'));
      }
    });
  }

  deleteContractor(contractor: Contractor): void {
    // 第一步：詳細警告說明
    const warningMessage = `⚠️ 重要警告 ⚠️\n\n您即將刪除承攬商：${contractor.companyName}\n\n此操作將會：\n• 永久刪除承攬商的所有基本資料\n• 永久刪除該承攬商旗下所有人員的資料\n• 刪除所有相關的證件檔案\n• 此操作無法復原\n\n如果您確定要繼續，請點擊「確定」`;

    if (confirm(warningMessage)) {
      // 第二步：要求輸入公司名稱確認
      const inputCompanyName = prompt(
        `請輸入承攬商公司名稱以確認刪除：\n\n公司名稱：${contractor.companyName}\n\n請在下方輸入完整的公司名稱：`
      );

      if (inputCompanyName === null) {
        // 用戶取消了操作
        return;
      }

      if (inputCompanyName.trim() === contractor.companyName.trim()) {
        // 名稱匹配，執行刪除
        this.deleteContractorViaAPI(contractor);
      } else {
        // 名稱不匹配
        alert('❌ 輸入的公司名稱不正確，刪除操作已取消。\n\n請確認輸入的公司名稱與要刪除的承攬商名稱完全一致。');
      }
    }
  }

  private deleteContractorViaAPI(contractor: Contractor): void {
    this.isDeleting = true; // 開始載入
    const companyid = environment.clientId;
    const apiUrl = `${environment.apiUrl}/api/contractor/delete`;

    // 準備 API 請求資料
    const requestData = {
      id: contractor.id,
      companyid: companyid
    };

    console.log('Deleting contractor via API:', requestData);

    this.http.delete<any>(apiUrl, { body: requestData }).subscribe({
      next: (response) => {
        this.isDeleting = false; // 結束載入
        if (response.success) {
          console.log('承攬商刪除成功:', response.message);

          // 更新本機資料
          const index = this.contractors.findIndex(c => c.id === contractor.id);
          if (index !== -1) {
            this.contractors.splice(index, 1);

            // If deleted contractor was selected, clear selection
            if (this.selectedContractor && this.selectedContractor.id === contractor.id) {
              this.selectedContractor = null;
              this.selectedContractorPersonnel = [];
            }

            // 同時刪除對應的人員資料
            delete this.personnelData[contractor.id];
          }

          alert(`✅ 刪除完成\n\n承攬商「${contractor.companyName}」及其所有人員資料已成功刪除。`);
          // 重新載入列表以確保資料同步
          this.loadContractorList();
        } else {
          console.error('API 回傳錯誤:', response);
          alert('刪除失敗：' + (response.message || '未知錯誤'));
        }
      },
      error: (error) => {
        this.isDeleting = false; // 結束載入
        console.error('API 呼叫失敗:', error);
        alert('刪除失敗：' + (error.error?.message || error.message || '網路連線錯誤'));
      }
    });
  }

  // Personnel operations
  openPersonnelModal(mode: 'add' | 'edit', person?: Personnel): void {
    this.isAddMode = mode === 'add';
    this.showPersonnelModal = true;

    // 重設照片
    this.selectedPhotoUrl = null;

    // 重置人員範本檔案狀態
    this.personnelTemplateFiles = {};
    this.personnelTemplateFileConfigs.forEach(config => {
      this.personnelTemplateFiles[config.type] = {
        type: config.type,
        uploaded: false
      };
    });

    if (mode === 'edit' && person) {
      this.editingPerson = person;
      this.personnelForm.patchValue({
        name: person.name,
        phone: person.phone,
        idNumber: person.idNumber,
        bloodType: person.bloodType,
        birthDate: person.birthDate,
        emergencyContact: person.emergencyContact,
        emergencyPhone: person.emergencyPhone,
        remarks: person.remarks || '',
        photo: person.photo, // 載入現有的 blobName
        ...this.extractFilesData(person.files) // 從 files 陣列中提取檔案資料和有效日期
      });

      // 如果有照片 blobName，載入顯示用的 URL
      if (person.photo) {
        this.getPhotoDisplayUrl(person.photo);
      }
    } else {
      this.editingPerson = null;
      this.personnelForm.reset();
    }
  }

  closePersonnelModal(): void {
    this.showPersonnelModal = false;
  }

   closePhotoDetailModal(): void {
    this.showPhotoDetailModal = false;
    this.selectedPersonForPhoto = null;
  }

  async savePerson(): Promise<void> {
    if (this.personnelForm.invalid || !this.selectedContractor || this.isSaving) return;

    const formData = this.personnelForm.value;

    if (this.isAddMode) {
      // Add new person
      const newId = this.guidService.generateGuid();

      const newPerson: Personnel = {
        id: newId,
        contractorId: this.selectedContractor.id,
        name: formData.name,
        photo: formData.photo, // 包含照片 blobName
        phone: formData.phone,
        idNumber: formData.idNumber,
        bloodType: formData.bloodType,
        birthDate: formData.birthDate,
        files: this.buildFilesArray(formData), // 建立統一的檔案陣列
        emergencyContact: formData.emergencyContact,
        emergencyPhone: formData.emergencyPhone,
        remarks: formData.remarks || ''
      };

      console.log('Saving new person:', newPerson);

      // 呼叫 API 儲存到資料庫
      await this.savePersonToDatabase(newPerson);
    } else if (this.editingPerson) {
      // Update existing person
      const index = this.selectedContractorPersonnel.findIndex(p => p.id === this.editingPerson!.id);
      if (index !== -1) {
        const updatedPerson = {
          ...this.editingPerson,
          name: formData.name,
          photo: formData.photo, // 包含照片 blobName
          phone: formData.phone,
          idNumber: formData.idNumber,
          bloodType: formData.bloodType,
          birthDate: formData.birthDate,
          files: this.buildFilesArray(formData), // 建立統一的檔案陣列
          emergencyContact: formData.emergencyContact,
          emergencyPhone: formData.emergencyPhone,
          remarks: formData.remarks || ''
        };

        // 呼叫 API 更新資料庫
        this.updatePersonInDatabase(updatedPerson);
      }
    }
  }

  // 新增人員到資料庫
  private async savePersonToDatabase(person: Personnel): Promise<void> {
    this.isSaving = true; // 開始儲存

    const companyid = environment.companyid;
    const requestData = {
      companyid: companyid,
      data: person
    };

    this.http.post<any>(`${environment.apiUrl}/api/contractor/employee/create`, requestData).subscribe({
      next: async (response) => {
        this.isSaving = false; // 結束儲存

        if (response.success) {
          console.log('人員資料儲存成功:', response.data);

          // 如果有照片，異步獲取照片 URL
          if (person.photo) {
            try {
              person.photoUrl = await this.getSecurePhotoUrl(person.photo);
            } catch (error) {
              console.error('獲取新增人員照片 URL 失敗:', error);
              person.photoUrl = undefined;
            }
          }

          // 更新本地資料
          this.selectedContractorPersonnel.push(person);

          // 更新人員資料對應表，確保指向同一個陣列避免重複
          this.personnelData[this.selectedContractor!.id] = this.selectedContractorPersonnel;

          // 關閉模態框
          this.closePersonnelModal();

          // 顯示成功訊息
          alert('人員資料新增成功！');
        } else {
          console.error('儲存失敗:', response);
          alert('人員資料儲存失敗，請重試');
        }
      },
      error: (error) => {
        this.isSaving = false; // 結束儲存
        console.error('儲存人員資料時發生錯誤:', error);
        alert('人員資料儲存失敗，請檢查網路連線並重試');
      }
    });
  }

  // 更新人員資料到資料庫
  private updatePersonInDatabase(person: Personnel): void {
    this.isSaving = true; // 開始儲存

    const companyid = environment.companyid;
    const updateData = {
      companyid: companyid,
      name: person.name,
      phone: person.phone,
      idNumber: person.idNumber,
      bloodType: person.bloodType,
      birthDate: person.birthDate,
      files: person.files,
      emergencyContact: person.emergencyContact,
      emergencyPhone: person.emergencyPhone,
      remarks: person.remarks || '',
      photo: person.photo
    };

    this.http.put<any>(`${environment.apiUrl}/api/contractor/employee/update/${person.id}`, updateData).subscribe({
      next: async (response) => {
        this.isSaving = false; // 結束儲存

        if (response.success) {
          console.log('人員資料更新成功:', response.data);

          // 如果有照片且照片有變更，異步獲取新的照片 URL
          if (person.photo) {
            try {
              person.photoUrl = await this.getSecurePhotoUrl(person.photo);
            } catch (error) {
              console.error('獲取更新人員照片 URL 失敗:', error);
              person.photoUrl = undefined;
            }
          } else {
            // 如果沒有照片，清除 photoUrl
            person.photoUrl = undefined;
          }

          // 更新本地資料
          const index = this.selectedContractorPersonnel.findIndex(p => p.id === person.id);
          if (index !== -1) {
            this.selectedContractorPersonnel[index] = { ...person, ...response.data };
          }

          // 更新人員資料對應表，確保指向同一個陣列避免重複更新
          this.personnelData[this.selectedContractor!.id] = this.selectedContractorPersonnel;

          // 關閉模態框
          this.closePersonnelModal();

          // 顯示成功訊息
          alert('人員資料更新成功！');
        } else {
          console.error('更新失敗:', response);
          alert('人員資料更新失敗，請重試');
        }
      },
      error: (error) => {
        this.isSaving = false; // 結束儲存
        console.error('更新人員資料時發生錯誤:', error);
        alert('人員資料更新失敗，請檢查網路連線並重試');
      }
    });
  }

  deletePerson(person: Personnel): void {
    if (confirm(`確定要刪除 ${person.name}?`)) {
      this.isDeleting = true; // 開始刪除

      const companyid = environment.companyid;
      const deleteData = {
        companyid: companyid
      };

      this.http.delete<any>(`${environment.apiUrl}/api/contractor/employee/delete/${person.id}`, {
        body: deleteData
      }).subscribe({
        next: (response) => {
          this.isDeleting = false; // 結束刪除

          if (response.success) {
            console.log('人員資料刪除成功:', response.message);

            // 從本地資料中移除
            const index = this.selectedContractorPersonnel.findIndex(p => p.id === person.id);
            if (index !== -1) {
              this.selectedContractorPersonnel.splice(index, 1);
            }

            // 更新人員資料對應表，確保指向同一個陣列
            this.personnelData[this.selectedContractor!.id] = this.selectedContractorPersonnel;

            // 顯示成功訊息
            alert('人員資料刪除成功！');
          } else {
            console.error('刪除失敗:', response);
            alert('人員資料刪除失敗，請重試');
          }
        },
        error: (error) => {
          this.isDeleting = false; // 結束刪除
          console.error('刪除人員資料時發生錯誤:', error);
          alert('人員資料刪除失敗，請檢查網路連線並重試');
        }
      });
    }
  }

  // File operations
  onFileSelected(event: any, fileType: string): void {
    const file = event.target.files[0];
    if (file) {
      // 檔案大小限制 (10MB for documents)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        alert('檔案大小不能超過 10MB');
        return;
      }

      // 檢查檔案類型
      const allowedTypes = ['.pdf', '.jpg', '.jpeg', '.png'];
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!allowedTypes.includes(fileExtension)) {
        alert('請選擇 PDF、JPG、JPEG 或 PNG 格式的檔案');
        return;
      }

      console.log(`開始上傳 ${fileType} 檔案:`, file.name);

      // 更新按鈕文字顯示上傳中狀態
      const label = event.target.nextElementSibling;
      if (label) {
        label.innerHTML = '<i class="fas fa-spinner fa-spin me-1"></i>上傳中...';
      }

      // 上傳檔案到 Azure Storage
      this.uploadDocumentToAzure(file, fileType, label);
    }
  }

  // 上傳證件檔案到 Azure Storage
  private uploadDocumentToAzure(file: File, fileType: string, buttonElement?: HTMLElement): void {
    const formData = new FormData();
    const companyid = environment.companyid;
    const filename = `${fileType}_${Date.now()}_${file.name}`;

    formData.append('file', file);
    formData.append('companyid', companyid);
    formData.append('filename', filename);
    formData.append('returnTempUrl', 'false'); // 證件檔案不需要立即顯示

    this.http.post<any>(`${environment.apiUrl}/api/storage/upload`, formData).subscribe({
      next: (response) => {
        if (response.success) {
          console.log(`${fileType} 上傳成功:`, response.data);

          // 將 blobName 儲存到對應的表單欄位
          const fieldName = `${fileType}File`;
          this.personnelForm.patchValue({
            [fieldName]: response.data.blobName
          });

          // 更新按鈕顯示
          if (buttonElement) {
            buttonElement.innerHTML = '<i class="fas fa-check me-1"></i>已上傳';
            buttonElement.className = 'btn btn-success btn-sm';
          }

          // 可選：顯示成功訊息
          // alert(`${this.getFileTypeDisplayName(fileType)} 上傳成功！`);
        } else {
          console.error('上傳失敗:', response);
          alert(`${this.getFileTypeDisplayName(fileType)} 上傳失敗，請重試`);
          this.resetUploadButton(buttonElement);
        }
      },
      error: (error) => {
        console.error(`上傳 ${fileType} 時發生錯誤:`, error);
        alert(`${this.getFileTypeDisplayName(fileType)} 上傳失敗，請檢查網路連線並重試`);
        this.resetUploadButton(buttonElement);
      }
    });
  }

  // 取得檔案類型的顯示名稱
  private getFileTypeDisplayName(fileType: string): string {
    const displayNames: { [key: string]: string } = {
      'trainingCert': '六小時教育訓練證明',
      'laborInsurance': '勞保證明',
      'groupInsurance': '團保證明',
      'healthCheck': '健康檢查'
    };
    return displayNames[fileType] || '檔案';
  }

  // 重設上傳按鈕狀態
  private resetUploadButton(buttonElement?: HTMLElement): void {
    if (buttonElement) {
      buttonElement.innerHTML = '<i class="fas fa-upload me-1"></i>瀏覽';
      buttonElement.className = 'btn btn-outline-primary btn-sm';
    }
  }

  uploadPhoto(person: Personnel): void {
    // 開啟編輯模式來上傳照片
    this.openPersonnelModal('edit', person);
  }

  viewPhoto(person: Personnel): void {
    if (!person.photo) {
      alert('此人員尚未上傳大頭照');
      return;
    }

    // 設定選中的人員並顯示照片詳細資訊模態框
    this.selectedPersonForPhoto = person;
    this.showPhotoDetailModal = true;
  }

  viewDocument(person: Personnel, docType: string): void {
    // 從 files 陣列中找到對應的檔案
    const file = person.files?.find(f => f.filetype === docType);

    if (!file || !file.url) {
      alert(`此人員尚未上傳${this.getFileTypeDisplayName(docType)}`);
      return;
    }

    // 使用 POST /api/storage/view-url API 取得瀏覽連結
    const requestData = {
      blobName: file.url,
      expiryMinutes: 60 // 設定 URL 有效時間為 60 分鐘
    };

    this.http.post<any>(`${environment.apiUrl}/api/storage/view-url`, requestData).subscribe({
      next: (response) => {
        if (response.success && response.data?.viewUrl) {
          // 直接在新分頁中開啟檔案
          window.open(response.data.viewUrl, '_blank');
          console.log('檔案瀏覽連結已開啟:', {
            blobName: response.data.blobName,
            expiresAt: response.data.expiresAt,
            viewType: response.data.viewType
          });
        } else {
          console.error('獲取檔案瀏覽連結失敗:', response);
          alert('無法載入檔案，請稍後再試');
        }
      },
      error: (error) => {
        console.error('載入檔案時發生錯誤:', error);
        alert('載入檔案失敗，請檢查網路連線並重試');
      }
    });
  }

  // 照片處理方法
  triggerPhotoUpload(): void {
    const photoInput = document.querySelector('#photoInput') as HTMLInputElement;
    if (photoInput) {
      photoInput.click();
    }
  }

  onPhotoSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];

    if (file) {
      // 檢查檔案類型
      if (!file.type.startsWith('image/')) {
        alert('請選擇圖片檔案');
        return;
      }

      // 檢查檔案大小 (5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert('檔案大小不能超過 5MB');
        return;
      }

      // 立即建立預覽URL
      const reader = new FileReader();
      reader.onload = (e) => {
        this.selectedPhotoUrl = e.target?.result as string;
      };
      reader.readAsDataURL(file);

      // 上傳檔案到 Azure Storage
      this.uploadPhotoToAzure(file);
    }
  }

  // 上傳照片到 Azure Storage
  private uploadPhotoToAzure(file: File): void {
    const formData = new FormData();
    const companyid = environment.companyid;
    const filename = `photo_${Date.now()}_${file.name}`;

    formData.append('file', file);
    formData.append('companyid', companyid);
    formData.append('filename', filename);
    formData.append('returnTempUrl', 'true'); // 取得臨時URL用於顯示

    // 顯示上傳中狀態
    console.log('開始上傳大頭照到 Azure Storage...');

    this.http.post<any>(`${environment.apiUrl}/api/storage/upload`, formData).subscribe({
      next: (response) => {
        if (response.success) {
          console.log('大頭照上傳成功:', response.data);

          // 儲存 blobName 到表單中（用於永久儲存）
          this.personnelForm.patchValue({
            photo: response.data.blobName
          });

          // 如果有臨時URL，更新預覽圖片
          if (response.data.tempUrl) {
            this.selectedPhotoUrl = response.data.tempUrl;
          }

          // 可選：顯示成功訊息
          // alert('大頭照上傳成功！');
        } else {
          console.error('上傳失敗:', response);
          alert('大頭照上傳失敗，請重試');
        }
      },
      error: (error) => {
        console.error('上傳大頭照時發生錯誤:', error);
        alert('大頭照上傳失敗，請檢查網路連線並重試');
      }
    });
  }

  // 從 blobName 取得顯示用的臨時 URL
  private getPhotoDisplayUrl(blobName: string): void {
    if (!blobName) return;

    // 使用 POST /api/storage/secure-url API 來取得照片顯示 URL
    const requestData = {
      blobName: blobName,
      expiryMinutes: 60
    };

    this.http.post<any>(`${environment.apiUrl}/api/storage/secure-url`, requestData).subscribe({
      next: (response) => {
        if (response.success && response.data?.secureUrl) {
          this.selectedPhotoUrl = response.data.secureUrl;
        } else {
          console.error('取得照片顯示 URL 失敗:', response);
        }
      },
      error: (error) => {
        console.error('取得照片顯示 URL 失敗:', error);
      }
    });
  }

  // Tag 型承攬商類別輸入相關處理方法
  onContractorTypeKeyDown(event: KeyboardEvent): void {
    const inputElement = event.target as HTMLInputElement;
    const value = inputElement.value.trim();

    if (event.key === 'Enter' && value) {
      event.preventDefault();
      this.addContractorTypeFromInput();
    } else if (event.key === 'Backspace' && !value && this.selectedContractorTypes.length > 0) {
      // 當輸入框為空且按下退格鍵時，刪除最後一個標籤
      this.selectedContractorTypes.pop();
      this.updateContractorTypeFormValue();
    }

    // 更新下拉選項
    setTimeout(() => {
      this.updateContractorTypeDropdown();
    });
  }

  addContractorTypeFromInput(): void {
    const value = this.contractorTypeInput.trim();
    if (value && !this.selectedContractorTypes.includes(value)) {
      this.selectedContractorTypes.push(value);
      this.contractorTypeInput = '';
      this.showContractorTypeDropdown = false;
      this.updateContractorTypeFormValue();
    }
  }

  selectContractorType(type: string): void {
    if (!this.selectedContractorTypes.includes(type)) {
      this.selectedContractorTypes.push(type);
      this.contractorTypeInput = '';
      this.showContractorTypeDropdown = false;
      this.updateContractorTypeFormValue();
    }
  }

  removeContractorType(type: string): void {
    const index = this.selectedContractorTypes.indexOf(type);
    if (index > -1) {
      this.selectedContractorTypes.splice(index, 1);
      this.updateContractorTypeFormValue();
    }
  }

  updateContractorTypeDropdown(): void {
    const input = this.contractorTypeInput.toLowerCase();
    if (input) {
      this.filteredContractorTypeOptions = this.availableContractorTypes.filter(type =>
        type.toLowerCase().includes(input) && !this.selectedContractorTypes.includes(type)
      );
      this.showContractorTypeDropdown = this.filteredContractorTypeOptions.length > 0 || input.length > 0;
    } else {
      this.filteredContractorTypeOptions = this.availableContractorTypes.filter(type =>
        !this.selectedContractorTypes.includes(type)
      );
      this.showContractorTypeDropdown = false;
    }
  }

  onContractorTypeInputFocus(): void {
    this.updateContractorTypeDropdown();
    this.showContractorTypeDropdown = true;
  }

  onContractorTypeInputBlur(): void {
    // 延遲關閉下拉選單，讓點擊選項有時間觸發
    setTimeout(() => {
      this.showContractorTypeDropdown = false;
      // 如果有輸入內容，則自動新增
      if (this.contractorTypeInput.trim()) {
        this.addContractorTypeFromInput();
      }
    }, 200);
  }

  focusContractorTypeInput(): void {
    // 使用 ViewChild 或手動查找輸入框並聚焦
    const inputElement = document.querySelector('.tag-input') as HTMLInputElement;
    if (inputElement) {
      inputElement.focus();
    }
  }

  private updateContractorTypeFormValue(): void {
    // 直接將選擇的類別陣列儲存到表單中
    this.contractorForm.patchValue({ contractorType: [...this.selectedContractorTypes] });
  }

  // 建立檔案陣列的輔助方法
  private buildFilesArray(formData: any): PersonnelFile[] {
    const files: PersonnelFile[] = [];

    const fileTypes = [
      { type: 'trainingCert', fileField: 'trainingCertFile', validField: 'trainingCertExpiryDate' },
      { type: 'laborInsurance', fileField: 'laborInsuranceFile', validField: 'laborInsuranceExpiryDate' },
      { type: 'groupInsurance', fileField: 'groupInsuranceFile', validField: 'groupInsuranceExpiryDate' },
      { type: 'healthCheck', fileField: 'healthCheckFile', validField: 'healthCheckExpiryDate' }
    ];

    fileTypes.forEach(config => {
      if (formData[config.fileField]) {
        const expiryDate = formData[config.validField];
        const isValid = this.isDateValid(expiryDate); // 檢查日期是否仍然有效

        files.push({
          filetype: config.type,
          url: formData[config.fileField],
          valid: isValid,
          expiryDate: expiryDate || null // 加入有效日期
        });
      }
    });

    return files;
  }

  // 檢查日期是否仍然有效的輔助方法
  private isDateValid(dateString: string): boolean {
    if (!dateString) return false;

    try {
      const expiryDate = new Date(dateString);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // 設定為今天的開始時間

      return expiryDate >= today;
    } catch (error) {
      console.error('日期格式錯誤:', dateString, error);
      return false;
    }
  }

  // 從 files 陣列中提取檔案資料的輔助方法
  private extractFilesData(files: PersonnelFile[]): any {
    const result: any = {
      trainingCertFile: '',
      laborInsuranceFile: '',
      groupInsuranceFile: '',
      healthCheckFile: '',
      trainingCertExpiryDate: '',
      laborInsuranceExpiryDate: '',
      groupInsuranceExpiryDate: '',
      healthCheckExpiryDate: ''
    };

    if (files && files.length > 0) {
      files.forEach(file => {
        switch (file.filetype) {
          case 'trainingCert':
            result.trainingCertFile = file.url;
            result.trainingCertExpiryDate = file.expiryDate || '';
            break;
          case 'laborInsurance':
            result.laborInsuranceFile = file.url;
            result.laborInsuranceExpiryDate = file.expiryDate || '';
            break;
          case 'groupInsurance':
            result.groupInsuranceFile = file.url;
            result.groupInsuranceExpiryDate = file.expiryDate || '';
            break;
          case 'healthCheck':
            result.healthCheckFile = file.url;
            result.healthCheckExpiryDate = file.expiryDate || '';
            break;
        }
      });
    }

    return result;
  }

  // 檢查特定類型的檔案是否存在
  hasFile(person: Personnel, fileType: string): boolean {
    return person.files?.some(f => f.filetype === fileType && f.url) || false;
  }

  // 檢查特定類型的檔案是否有效
  isFileValid(person: Personnel, fileType: string): boolean {
    const file = person.files?.find(f => f.filetype === fileType);
    if (!file) return false;

    // 如果有 valid 屬性，直接使用
    if (file.hasOwnProperty('valid')) {
      return file.valid;
    }

    // 如果沒有 valid 屬性，則根據有效日期判斷
    if (file.expiryDate) {
      return this.isDateValid(file.expiryDate);
    }

    return false;
  }

  // 獲取檔案的有效日期
  getFileExpiryDate(person: Personnel, fileType: string): string {
    const file = person.files?.find(f => f.filetype === fileType);
    if (!file || !file.expiryDate) {
      return '未設定';
    }

    try {
      const expiryDate = new Date(file.expiryDate);
      // 直接顯示日期，不顯示額外的文字說明
      return expiryDate.toLocaleDateString('zh-TW');
    } catch (error) {
      console.error('日期格式錯誤:', file.expiryDate, error);
      return '日期格式錯誤';
    }
  }

  // 生成照片 URL
  generatePhotoUrl(blobName: string): string {
    if (!blobName) return '';

    // 使用 GET /api/storage/file/:blobName API
    // URL 編碼 blobName 以處理特殊字符
    const encodedBlobName = encodeURIComponent(blobName);
    return `${environment.apiUrl}/api/storage/file/${encodedBlobName}?expiryMinutes=120`;
  }

  // 獲取安全照片 URL
  async getSecurePhotoUrl(blobName: string): Promise<string> {
    if (!blobName) return '';

    try {
      const apiUrl = `${environment.apiUrl}/api/storage/secure-url`;
      const response = await firstValueFrom(this.http.post<any>(apiUrl, {
        blobName: blobName,
        expiryMinutes: 120,
        forInlineView: true  //加入這個參數，就可以在瀏覽器中直接看檔案
      }));

      if (response?.success && response?.data?.secureUrl) {
        return response.data.secureUrl;
      } else {
        throw new Error('無法獲取安全 URL');
      }
    } catch (error) {
      console.error('獲取安全照片 URL 失敗:', error);
      throw error;
    }
  }

  // 照片錯誤處理
  onPhotoError(event: any, person: Personnel): void {
    console.error('照片載入失敗:', person.photo);
    // 清除錯誤的照片 URL，讓使用者可以重新上傳
    person.photoUrl = undefined;
  }

  // 檢查是否有現有檔案
  hasExistingFile(fileType: string): boolean {
    if (!this.editingPerson || !this.editingPerson.files) return false;

    const file = this.editingPerson.files.find(f => f.filetype === fileType);
    return !!(file && file.url);
  }

  // 檢視現有檔案
  viewExistingFile(fileType: string): void {
    if (!this.editingPerson) return;

    this.viewDocument(this.editingPerson, fileType);
  }

  // 從 URL 中提取檔案名稱的輔助方法
  extractFileNameFromUrl(url: string): string {
    if (!url) return '';

    // 從 Azure Blob URL 中提取檔案名稱
    // URL 格式通常為: companyid/guid_filetype_timestamp_originalfilename
    const parts = url.split('/');
    const fileName = parts[parts.length - 1]; // 取得最後一部分

    // 嘗試提取原始檔案名稱
    const underscoreIndex = fileName.lastIndexOf('_');
    if (underscoreIndex !== -1) {
      return fileName.substring(underscoreIndex + 1);
    }

    return fileName;
  }

  // 取得表單中對應的檔案名稱
  getFormFileName(fileType: string): string {
    const fieldName = `${fileType}File`;
    const url = this.personnelForm.get(fieldName)?.value;
    return this.extractFileNameFromUrl(url);
  }

  // =============== 範本檔案處理相關方法 ===============

  /**
   * 處理範本檔案選擇
   */
  onTemplateFileSelected(fileType: string, file: File) {
    if (this.templateFiles[fileType]) {
      this.templateFiles[fileType].file = file;
      this.templateFiles[fileType].uploaded = true;

      // 這裡可以添加檔案上傳到伺服器的邏輯
      console.log(`${fileType} 檔案已選擇:`, file.name);
    }
  }

  /**
   * 處理範本檔案移除
   */
  onTemplateFileRemoved(fileType: string) {
    if (this.templateFiles[fileType]) {
      this.templateFiles[fileType].file = undefined;
      this.templateFiles[fileType].uploaded = false;

      // 這裡可以添加從伺服器刪除檔案的邏輯
      console.log(`${fileType} 檔案已移除`);
    }
  }

  /**
   * 處理範本檔案下載
   */
  onTemplateDownloaded(fileType: string, templateUrl: string) {
    console.log(`${fileType} 範本已下載:`, templateUrl);

    // 這裡可以添加下載記錄的邏輯
    // 例如：記錄使用者下載了哪些範本檔案
  }

  /**
   * 檢查必填範本檔案是否都已上傳
   */
  areRequiredTemplateFilesUploaded(): boolean {
    return this.templateFiles['basicInfo'].uploaded &&
           this.templateFiles['safetyPlan'].uploaded;
  }

  /**
   * 取得已上傳的範本檔案數量
   */
  getUploadedTemplateFilesCount(): number {
    return Object.values(this.templateFiles).filter(file => file.uploaded).length;
  }

  /**
   * 處理人員範本檔案選擇
   */
  onPersonnelTemplateFileSelected(file: File, type: string): void {
    if (this.personnelTemplateFiles[type]) {
      // 更新狀態
      this.personnelTemplateFiles[type] = {
        type: type,
        file: file,
        uploaded: true
      };
      console.log('人員範本檔案已選擇:', { type, fileName: file.name });
    }
  }

  /**
   * 處理人員範本檔案移除
   */
  onPersonnelTemplateFileRemoved(type: string): void {
    if (this.personnelTemplateFiles[type]) {
      this.personnelTemplateFiles[type] = {
        type: type,
        uploaded: false
      };
      console.log('人員範本檔案已移除:', type);
    }
  }

  /**
   * 處理人員範本檔案下載
   */
  onPersonnelTemplateDownloaded(url: string): void {
    console.log('人員範本檔案已下載:', url);
  }

  /**
   * 處理 acceptedFormats 資料格式
   * @param acceptedFormats 可能是字串或陣列格式
   * @returns 處理後的字串陣列
   */
  private processAcceptedFormats(acceptedFormats: any): string[] {
    // 如果已經是陣列，直接返回
    if (Array.isArray(acceptedFormats)) {
      return acceptedFormats;
    }

    // 如果是字串，進行分割處理
    if (typeof acceptedFormats === 'string' && acceptedFormats.trim()) {
      return acceptedFormats.split(',').map((format: string) => format.trim());
    }

    // 預設格式
    return ['.pdf', '.doc', '.docx'];
  }


  triggerFileUpload(fileType: string): void {

    console.log(`觸發 ${fileType}Input 檔案上傳...`);

    const fileInput = document.querySelector(`#${fileType}Input`) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  /**
   * 處理 showlist 資料更新事件
   */
  onShowlistDataUpdated(): void {
    console.log('Showlist 資料已更新');
    // 這裡可以根據需要重新載入相關資料
    // 例如重新載入承攬商列表或其他相關資料
    this.loadContractorList();
  }

}
